import React, { useState, useEffect } from 'react';
import { updateStopLossApi } from '../api/api';

const UpdateStopLossModal = ({ isOpen, onClose, trade, onSuccessfulUpdate }) => {
  const [slUpdateType, setSlUpdateType] = useState('specific_price'); // Default type
  const [value, setValue] = useState('');
  const [specificPrice, setSpecificPrice] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // Reset form when trade changes or modal opens/closes
    if (isOpen && trade) {
      setSlUpdateType('specific_price');
      setValue('');
      setSpecificPrice(trade.stop_loss ? String(trade.stop_loss) : ''); // Pre-fill with current SL if available
      setError(null);
      setSuccessMessage('');
    } else if (!isOpen) {
        // Clear fields when modal is closed
        setSlUpdateType('specific_price');
        setValue('');
        setSpecificPrice('');
        setError(null);
        setSuccessMessage('');
    }
  }, [isOpen, trade]);

  if (!isOpen || !trade) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccessMessage('');

    const payload = {
      trade_id: trade.trade_id,
      sl_update_type: slUpdateType,
    };

    if (slUpdateType === 'distance_pips' || slUpdateType === 'distance_price') {
      if (!value || isNaN(parseFloat(value))) {
        setError({ detail: { value: ['This field is required and must be a number.'] } });
        setIsLoading(false);
        return;
      }
      payload.value = String(parseFloat(value));
    } else if (slUpdateType === 'specific_price') {
      if (!specificPrice || isNaN(parseFloat(specificPrice))) {
        setError({ detail: { specific_price: ['This field is required and must be a number.'] } });
        setIsLoading(false);
        return;
      }
      payload.specific_price = String(parseFloat(specificPrice));
    }
    // For 'breakeven', no 'value' or 'specific_price' is needed.

    try {
      const response = await updateStopLossApi(payload);
      setSuccessMessage(response.message || 'Stop loss updated successfully!');
      if (onSuccessfulUpdate) {
        onSuccessfulUpdate(); // This should refresh data and close modal via Dashboard
      }
      // Optionally close modal after a short delay or let Dashboard handle it
      // setTimeout(onClose, 2000);
    } catch (apiError) {
      console.error("Update SL Error from API:", apiError);
      // apiError is expected to be the response.data from the backend
      // It could be { detail: "message" }, { detail: { field: ["message"] } }, or { error: "message" }
      if (apiError && (apiError.detail || apiError.error)) {
        setError(apiError); // Store the whole error object from backend
      } else if (typeof apiError === 'string') {
        setError({ detail: apiError }); // Handle if apiError itself is a string message
      }
      else {
        setError({ detail: 'An unexpected error occurred. Please try again.' }); // Fallback
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to display field-specific error details
  const getFieldErrorMessage = (field) => {
    if (error && typeof error.detail === 'object' && error.detail[field] && Array.isArray(error.detail[field])) {
      return error.detail[field].join(' ');
    }
    return null;
  };

  // Helper to display general (non-field specific) errors
  const getGeneralErrorMessage = () => {
    if (error) {
      if (typeof error.detail === 'string') {
        return error.detail;
      }
      // Handle cases like: { "detail": ["message1", "message2"] }
      if (Array.isArray(error.detail) && error.detail.every(item => typeof item === 'string')) {
        return error.detail.join(' ');
      }
      if (typeof error.error === 'string') {
        return error.error;
      }
      // If error.detail is an object (field errors), those are handled by getFieldErrorMessage.
      // This function is for general messages.
    }
    return null;
  };

  const generalError = getGeneralErrorMessage();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-backgroundSurface p-6 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-textPrimary">Update Stop Loss for Trade {trade.position_id}</h2>
          <button onClick={onClose} className="text-textSecondary hover:text-textPrimary">&times;</button>
        </div>

        {successMessage && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{successMessage}</div>}
        {generalError && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{generalError}</div>}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="slUpdateType" className="block text-sm font-medium text-textSecondary mb-1">Update Type</label>
            <select
              id="slUpdateType"
              value={slUpdateType}
              onChange={(e) => {
                setSlUpdateType(e.target.value);
                setValue(''); // Reset other fields on type change
                setSpecificPrice(e.target.value === 'specific_price' && trade.stop_loss ? String(trade.stop_loss) : '');
                setError(null); // Clear previous errors
              }}
              className="w-full p-2 border border-borderDefault rounded-md bg-backgroundInput text-textPrimary focus:ring-primary focus:border-primary"
            >
              <option value="specific_price">Specific Price</option>
              <option value="breakeven">Move to Breakeven</option>
              <option value="distance_pips">Distance in Pips from Current</option>
              <option value="distance_price">Distance in Price from Current</option>
            </select>
          </div>

          {slUpdateType === 'distance_pips' && (
            <div className="mb-4">
              <label htmlFor="value_pips" className="block text-sm font-medium text-textSecondary mb-1">Distance (Pips)</label>
              <input
                type="number"
                step="any"
                id="value_pips"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full p-2 border border-borderDefault rounded-md bg-backgroundInput text-textPrimary"
                placeholder="e.g., 20.0"
              />
              {getFieldErrorMessage('value') && <p className="text-xs text-destructive mt-1">{getFieldErrorMessage('value')}</p>}
            </div>
          )}

          {slUpdateType === 'distance_price' && (
            <div className="mb-4">
              <label htmlFor="value_price" className="block text-sm font-medium text-textSecondary mb-1">Distance (Price Units)</label>
              <input
                type="number"
                step="any"
                id="value_price"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full p-2 border border-borderDefault rounded-md bg-backgroundInput text-textPrimary"
                placeholder="e.g., 0.00050"
              />
              {getFieldErrorMessage('value') && <p className="text-xs text-destructive mt-1">{getFieldErrorMessage('value')}</p>}
            </div>
          )}

          {slUpdateType === 'specific_price' && (
            <div className="mb-4">
              <label htmlFor="specificPrice" className="block text-sm font-medium text-textSecondary mb-1">Specific Stop Loss Price</label>
              <input
                type="number"
                step="any"
                id="specificPrice"
                value={specificPrice}
                onChange={(e) => setSpecificPrice(e.target.value)}
                className="w-full p-2 border border-borderDefault rounded-md bg-backgroundInput text-textPrimary"
                placeholder="e.g., 1.12345"
              />
              {getFieldErrorMessage('specific_price') && <p className="text-xs text-destructive mt-1">{getFieldErrorMessage('specific_price')}</p>}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="btn btn-secondary px-4 py-2 rounded-md text-textPrimary hover:bg-secondary/80 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary px-4 py-2 rounded-md text-white bg-primary hover:bg-primary/90 disabled:opacity-50"
            >
              {isLoading ? 'Updating...' : 'Update Stop Loss'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateStopLossModal;
